import * as React from 'react';
import { connect, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';
import { createTheme } from '@rtkwlf/ui';
import { ThemeProvider } from 'styled-components';
import { SESSION_ACTIONS } from './actions/session';
import { apiRequest } from './APIClient';
import {
  RedirectToDashboard,
  RedirectToDefault,
  RedirectToLogsearch,
  RedirectToLogin,
  RedirectToMSP,
  ReloadWindow,
  ROUTE_PATH,
  setAuthTokenFromCookie,
  UUID_REGEX,
} from './constants';
import './global.scss';
import { sessionSelector } from './reducers/session';
import Share from './routes/contact/ShareView';
import MSPCustomerSwitchView from './routes/customers/MSPCustomerSwitchView';
import DashboardRouteManager from './routes/dashboard/DashboardRouteManager';
import DataRouteManager from './routes/data/DataRouteManager';
import { authorizedTo } from './security/withAuthorization';
import {
  Dispatch,
  GlobalStatusType,
  PermissionEnum,
  SessionStorageKey,
  StatusType,
  UserRole,
  UserRoleEnum,
} from './types';
import MaskedLoadingIndicator from './widgets/MaskedLoadingIndicator';
import OnboardingRouteManager from './routes/onboarding/OnboardingRouteManager';
import CustomerSwitchView from './routes/customers/CustomerSwitchView';
import ConnectedAccountRouteManager from './routes/self-service/connected-accounts/ConnectedAccountRouteManager';
import CustomerPodUrl from './routes/customers/CustomerPodUrl';
import {
  Auth0ProviderOptions,
  CacheLocation,
  WithAuthenticationRequiredOptions,
} from '@auth0/auth0-react';
import {
  memoizedSelectRolesAndServices,
  RolesAndServices,
} from './selectors/selectRolesAndServices';
import ContactRouteManager from './routes/contact/ContactRouteManager';
import AppWithLoginPage from './AppWithLoginPage';
import AuthorizedRoute from './security/AuthorizedRoute';
import LoginStatus from './security/LoginStatus';
import AppWithStatus from './AppWithStatus';
import { ThemeProvider as FenrirThemeProvider } from '@rtkwlf/fenrir-react';
import { withFenrirLightTheme } from './hooks/withFenrirLightTheme';
import StatusView from './routes/dashboard/status/StatusView';
import DeviceConfigView from './routes/onboarding/site-config/v2/sensor/DeviceConfigView';
import * as FullStory from '@fullstory/browser';

const theme = createTheme(
  {},
  {
    typographyLabelTextTransform: 'none',
    typographyLabelFontWeight: 'normal',
  },
);

// Define the authorized routes as constants so they are not re-created each render
const ContactRoute = authorizedTo.contactCST(
  ContactRouteManager,
  RedirectToDefault,
);
const DashboardRoute = authorizedTo.viewDashboard(
  DashboardRouteManager,
  RedirectToDefault,
);
const DefaultRoute = authorizedTo.viewOnboarding(
  OnboardingRouteManager,
  authorizedTo.switchMspOwnCustomers(
    RedirectToMSP,
    authorizedTo.viewDashboard(
      RedirectToDashboard,
      authorizedTo.viewRawLogs(RedirectToLogsearch, RedirectToLogin),
    ),
  ),
);
const ShareRoute = authorizedTo.contactCST(Share, RedirectToDefault);

const ConnectedAccountsRoute = authorizedTo.manageCredentials(
  ConnectedAccountRouteManager,
  RedirectToDefault,
);
const CustomerSwitchRoute = authorizedTo.switchAllCustomers(
  CustomerSwitchView,
  RedirectToDefault,
);
const CustomerPodUrlRoute = authorizedTo.switchAllCustomers(
  CustomerPodUrl,
  RedirectToDefault,
);
const MSPCustomerSwitchRoute = authorizedTo.switchMspOwnCustomers(
  MSPCustomerSwitchView,
  RedirectToDefault,
);
const OnboardingRoute = authorizedTo.viewOnboarding(
  OnboardingRouteManager,
  RedirectToDashboard,
);

const RedirectToServiceEndpoints = () => (
  <Redirect to={ROUTE_PATH.SERVICE_ENDPOINTS} />
);

const RedirectToCoverageScore = () => (
  <Redirect to={ROUTE_PATH.DASHBOARD.COVERAGE_SCORE} />
);

type OwnProps = RouteComponentProps;

export type StoreProps = {
  customerName: string | null;
  currentCustomerId: string | null;
  globalStatus: Array<GlobalStatusType>;
  isSwitchingCustomer: boolean;
  username: string | null;
  logoutStatus: StatusType | null;
  roles: Array<UserRole>;
  rolesAndServices: RolesAndServices;
  useFrontendAuth0: boolean;
  isLegacyLoginPage: boolean;
};

export type AppProps = {
  isSwitchingCustomer: boolean;
  globalStatus: Array<GlobalStatusType>;
  username: string | null;
  useFrontendAuth0: boolean;
  rolesAndServices: RolesAndServices;
  clearGlobalStatus: (serial: number) => void;
};

export type DispatchProps = {
  clearGlobalStatus: (serial: number) => void;
  switchCustomer: (arg0: string) => Promise<any>;
  switchMSPCustomer: (
    id: string,
    riskID: string | undefined,
    nextRoute: string,
  ) => void;
  validate: () => void;
  getIncidentInfo: () => void;
};

const AUTH0_CONFIG: Auth0ProviderOptions = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN || '',
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID || '',
  redirectUri: window.location.origin,
  useRefreshTokens: true,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE || '',
  cacheLocation: (process.env.REACT_APP_AUTH0_CACHE_LOCATION ||
    'memory') as CacheLocation,
};

const AUTH0_LOGIN_CONFIG: WithAuthenticationRequiredOptions = {
  onRedirecting: () => {
    return <MaskedLoadingIndicator message={'Logging In'} />;
  },
  loginOptions: {
    appState: {
      returnTo: window.location.pathname,
    },
  },
};

export type Props = OwnProps & StoreProps & DispatchProps;

class App extends React.Component<Props> {
  componentDidMount() {
    const { history, validate } = this.props;

    window.addEventListener('beforeunload', this.removeLogoutMessage);
    window.sessionStorage.removeItem(SessionStorageKey.LOGGED_OUT);

    // Upon mount of root app, put the ?impersonate parameter into
    // local storage if it exists, so that we can retrieve it after an OAuth
    // workflow (where we are redirected away and back, losing application state)
    if (history.location.search) {
      const params = new URLSearchParams(history.location.search);
      const customerId = params.get('impersonate') || params.get('cid');
      if (customerId) {
        window.sessionStorage.setItem(
          SessionStorageKey.IMPERSONATE,
          customerId,
        );
      }
    }

    // Also upon mounting, re-validate an existing token, if present
    if (window.sessionStorage.getItem(SessionStorageKey.PRIMARY_TOKEN)) {
      validate();
    } else {
      const { primaryCookieJWT, secondaryCookieJWT } = setAuthTokenFromCookie();
      // User just got redirected back after a successful Auth0 login
      if (primaryCookieJWT || secondaryCookieJWT) {
        // this flag is used upon logout to decide whether to call auth0 API
        // it will be removed by the reducer
        window.sessionStorage.setItem(SessionStorageKey.USE_AUTH0, 'true');
        validate();
      }
    }

    if (
      localStorage.getItem('FULLSTORY') === 'true' &&
      !FullStory.isInitialized()
    ) {
      FullStory.init({ orgId: 'o-1JY8D8-na1' });
    }

    this.props.getIncidentInfo();
  }

  componentDidUpdate(prevProps: Props) {
    const {
      isSwitchingCustomer,
      currentCustomerId,
      history,
      switchCustomer,
      switchMSPCustomer,
      username,
      customerName,
      roles,
    } = this.props;

    // No need to switch customers/impersonate for the device configuration route.
    if (history.location.pathname === ROUTE_PATH.DEVICE_CONFIGURE) {
      return;
    }

    if (prevProps.username !== username) {
      // username changed...
      if (username) {
        // ...to something - login; check for impersonation and switch customer
        const customerId = window.sessionStorage.getItem(
          SessionStorageKey.IMPERSONATE,
        );
        if (customerId) {
          window.sessionStorage.removeItem(SessionStorageKey.IMPERSONATE);
          const params = new URLSearchParams(history.location.search);
          if (params.has('impersonate') || params.has('cid')) {
            params.delete('impersonate');
            params.delete('cid');
            history.replace({
              ...history.location,
              search: params.toString(),
            });
          }
          if (
            roles.includes(UserRoleEnum.VIEW_CUST_SWITCH) &&
            customerId !== currentCustomerId
          ) {
            switchCustomer(customerId);
          } else if (roles.includes(UserRoleEnum.VIEW_MSPSWITCH)) {
            switchMSPCustomer(customerId, undefined, ROUTE_PATH.MSP_DASHBOARD);
          }
        } else {
          if (
            !customerName &&
            roles.includes(UserRoleEnum.VIEW_CUST_SWITCH) &&
            history.location.pathname !== ROUTE_PATH.CUSTOMER_SWITCH
          ) {
            history.push(ROUTE_PATH.CUSTOMER_SWITCH);
          }
        }
      } else {
        // ...to nothing - logged out; do a refresh to make sure the application is the latest version
        if (!isSwitchingCustomer) {
          ReloadWindow();
        }
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.removeLogoutMessage);
  }

  removeLogoutMessage() {
    if (!window.sessionStorage.getItem(SessionStorageKey.LOGGED_OUT)) {
      window.sessionStorage.removeItem(SessionStorageKey.AUTH_MESSAGE);
    }
  }

  render() {
    const {
      isLegacyLoginPage,
      isSwitchingCustomer,
      clearGlobalStatus,
      globalStatus,
      username,
      useFrontendAuth0,
      rolesAndServices,
    } = this.props;

    return (
      <FenrirThemeProvider>
        <ThemeProvider theme={theme}>
          {isLegacyLoginPage ? (
            <AppWithLoginPage
              isSwitchingCustomer={isSwitchingCustomer}
              clearGlobalStatus={clearGlobalStatus}
              globalStatus={globalStatus}
              username={username}
              useFrontendAuth0={useFrontendAuth0}
              rolesAndServices={rolesAndServices}
              auth0Config={AUTH0_CONFIG}
              loginConfig={AUTH0_LOGIN_CONFIG}
            />
          ) : (
            <AppWithStatus
              useFrontendAuth0
              isSwitchingCustomer={isSwitchingCustomer}
              clearGlobalStatus={clearGlobalStatus}
              globalStatus={globalStatus}
              username={username}
              rolesAndServices={rolesAndServices}
              auth0Config={AUTH0_CONFIG}
              loginConfig={AUTH0_LOGIN_CONFIG}
            />
          )}
        </ThemeProvider>
      </FenrirThemeProvider>
    );
  }
}

// What you see when you're logged in
export const AwthenticatedRoutes = () => {
  const orgID = useSelector(sessionSelector.getRiskID);
  const orgIDParam = orgID ? `?id=${orgID}` : '';

  return (
    <Switch>
      <Route path={ROUTE_PATH.ONBOARDING.MAIN} component={OnboardingRoute} />
      <Route path={ROUTE_PATH.DASHBOARD.MAIN} component={DashboardRoute} />
      <AuthorizedRoute
        path={ROUTE_PATH.DATA.MAIN}
        permission={PermissionEnum.VIEW_DATA}
        component={DataRouteManager}
      />
      <Route
        path={ROUTE_PATH.TICKETS}
        render={() => {
          window.location.href = `${ROUTE_PATH.DASHBOARD.UNIFIED}/tickets${orgIDParam}`;
        }}
      />
      <Route exact path={ROUTE_PATH.SHARE} component={ShareRoute} />
      <Route path={ROUTE_PATH.CONTACT} component={ContactRoute} />
      <Route
        path={[
          ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.MAIN,
          ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.DEPRECATED,
        ]}
        component={ConnectedAccountsRoute}
      />

      <Route
        path={ROUTE_PATH.LOG_SEARCH.ACTIVITY}
        render={() => {
          window.location.href = `${ROUTE_PATH.DASHBOARD.UNIFIED}/data-explorer/raw-log/history${orgIDParam}`;
        }}
      />
      <Route
        path={ROUTE_PATH.LOG_SEARCH.LOG_SOURCES}
        render={() => {
          window.location.href = `${ROUTE_PATH.DASHBOARD.UNIFIED}/data-explorer/raw-log/sources${orgIDParam}`;
        }}
      />
      <Route
        path={`${ROUTE_PATH.LOG_SEARCH.MAIN}/:searchId(${UUID_REGEX})`}
        render={(routeProps) => {
          const { searchId } = routeProps.match.params;
          window.location.href = `${ROUTE_PATH.DASHBOARD.UNIFIED}/data-explorer/raw-log/search/${searchId}${orgIDParam}`;
        }}
      />
      <Route
        path={ROUTE_PATH.LOG_SEARCH.MAIN}
        render={() => {
          window.location.href = `${ROUTE_PATH.DASHBOARD.UNIFIED}/data-explorer/raw-log/search${orgIDParam}`;
        }}
      />

      <Route
        exact
        path={[
          ROUTE_PATH.INSTALLER, // for backward-compatibility
          ROUTE_PATH.DOWNLOADS.INTERNAL,
        ]}
        render={() => {
          window.location.href = `${ROUTE_PATH.DOWNLOADS.UNIFIED_PORTAL}${orgIDParam}`;
        }}
      />
      <Route
        exact
        path={ROUTE_PATH.MSP_DASHBOARD}
        component={MSPCustomerSwitchRoute}
      />
      <Route
        exact
        path={ROUTE_PATH.CUSTOMER_SWITCH}
        component={CustomerSwitchRoute}
      />
      <Route
        exact
        path={ROUTE_PATH.AWS_TEMPLATE}
        component={CustomerPodUrlRoute}
      />
      <Route
        path={ROUTE_PATH.REPORTS}
        render={() => {
          window.location.href = `${ROUTE_PATH.DASHBOARD.UNIFIED}/reports${orgIDParam}`;
        }}
      />
      <Route
        exact
        path={ROUTE_PATH.WHITELISTABLE_IPS} // deprecated route
        component={RedirectToServiceEndpoints}
      />
      <Route
        exact
        path={ROUTE_PATH.DASHBOARD.SECURITY_SCORE} // deprecated route
        component={RedirectToCoverageScore}
      />
      <Route
        exact
        path={ROUTE_PATH.SERVICE_ENDPOINTS}
        render={() => {
          window.location.href = `${ROUTE_PATH.DASHBOARD.UNIFIED}/resource-center/allow-list${orgIDParam}`;
        }}
      />
      <Route exact path={ROUTE_PATH.STATUS} component={StatusView} />
      <Route exact path={ROUTE_PATH.LOGIN} component={LoginStatus} />
      <Route
        exact
        path={ROUTE_PATH.DEVICE_CONFIGURE}
        component={DeviceConfigView}
      />
      <DefaultRoute />
    </Switch>
  );
};

export default withRouter(
  withFenrirLightTheme(
    connect<StoreProps, DispatchProps, OwnProps>(
      (store): StoreProps => ({
        customerName: sessionSelector.getCustomerName(store),
        globalStatus: sessionSelector.getGlobalStatus(store),
        isSwitchingCustomer: sessionSelector.isSwitchingCustomer(store),
        username: sessionSelector.getUsername(store),
        logoutStatus: sessionSelector.getLogoutStatus(store),
        roles: sessionSelector.getRoles(store),
        currentCustomerId: sessionSelector.getCustomerID(store),
        rolesAndServices: memoizedSelectRolesAndServices(store),
        useFrontendAuth0: sessionSelector.useFrontendAuth0(store),
        isLegacyLoginPage: sessionSelector.isLegacyLoginPage(store),
      }),
      (dispatch: Dispatch): DispatchProps => ({
        clearGlobalStatus: (serial: number) =>
          dispatch(SESSION_ACTIONS.CLEAR_GLOBAL_STATUS_WITH_SERIAL(serial)),
        switchCustomer: (id: string) =>
          dispatch(apiRequest.switchCustomer(id, undefined)),
        switchMSPCustomer: (
          id: string,
          riskID: string | undefined = undefined,
          nextRoute: string,
        ) => dispatch(apiRequest.switchMSPCustomer(id, riskID, nextRoute)),
        validate: () => dispatch(apiRequest.getUserInfo()),
        getIncidentInfo: () => dispatch(apiRequest.getIncidentInfo()),
      }),
    )(App),
  ),
);
